import { useState } from "react";

import AddCircleIcon from "@mui/icons-material/AddCircleRounded";
import { Box, Button, Checkbox, Divider, FormControlLabel, List, Stack, Typography } from "@mui/material";

import { AssignmentCreationDialog } from "@/common/components/assignments/assignment-creation/AssignmentCreationDialog";
import { AssignmentWidget } from "@/common/components/assignments/assignment-widget/AssignmentWidget";
import { useTranslation } from "@/translations";

import { useAssignments } from "./hooks/useAssignments";

export function Assignments() {
  const { t } = useTranslation();

  const {
    openAssignments,
    completedAssignments,
    handleDeleteAllCompleted,
    isDeletingAllCompleted,
    showCompleted,
    setShowCompleted,
  } = useAssignments();

  const [showCreationDialog, setShowCreationDialog] = useState(false);

  return (
    <Stack spacing={2} sx={{ height: "100%" }} pb={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h1">{t("assignments.assignmentsOverview", { count: openAssignments.length })}</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={showCompleted}
                onChange={(_, checked) => setShowCompleted(checked)}
                aria-label={t("assignments.showCompleted")}
              />
            }
            label={t("assignments.showCompleted")}
          />
          <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => setShowCreationDialog(true)}>
            {t("assignments.create")}
          </Button>
        </Stack>
        {showCreationDialog && <AssignmentCreationDialog onClose={() => setShowCreationDialog(false)} />}
      </Box>

      <Stack sx={{ flex: 1, minHeight: 0 }} spacing={4}>
        <List disablePadding sx={{ overflowY: "auto", flex: 1 }}>
          {openAssignments.map((assignment) => (
            <AssignmentWidget key={assignment.assignmentId} assignment={assignment} />
          ))}
        </List>

        {showCompleted && (
          <Stack sx={{ flex: 1, minHeight: 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} mx={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                {t("assignments.completedAssignment", { count: completedAssignments.length })}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleDeleteAllCompleted}
                disabled={isDeletingAllCompleted || completedAssignments.length === 0}
              >
                {t("assignments.deleteAllCompletedAssignments")}
              </Button>
            </Box>
            <Divider sx={{ mr: 2 }} />
            <List disablePadding sx={{ overflowY: "auto", flex: 1 }}>
              {completedAssignments.map((assignment) => (
                <AssignmentWidget key={assignment.assignmentId} assignment={assignment} />
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
